import React from "react";
import Select, {
  FormatOptionLabelMeta,
  GroupBase,
  StylesConfig,
} from "react-select";
import { ErrorMessage } from "formik";
import { getInputHeight } from "src/utils/formUtils/getInputHeight";
import makeAnimated from "react-select/animated";

export type Option = {
  label: string;
  value: string | number | string[];
};

type Props = {
  options: Option[];
  value?: Option[] | any[];
  onChange: (value: any) => void;
  isOptionEqualToValue?: (option: any, selected: any) => boolean;
  isSearchedOption?: (option: any, searchValue: string) => boolean;
  getOptionValue?: (option: any) => string;
  label?: string;
  required?: boolean;
  size?: "small" | "medium" | number;
  name: string;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  renderOption?: (
    option: Option | any,
    meta: FormatOptionLabelMeta<Option | any>
  ) => React.ReactNode;
  onBlur?: any;
  isMenuOpen?: boolean;
};

const CustomOption = ({ innerProps, data }: any) => {
  return (
    <div {...innerProps} className="custom-option">
      {data.value === "select-all" ? <strong>{data.label}</strong> : data.label}
    </div>
  );
};

const ATMMultiSelect = ({
  options = [],
  label = "",
  required = false,
  value,
  placeholder = `Select`,
  onChange,
  size = "small",
  name,
  isLoading = false,
  disabled = false,
  renderOption,
  isOptionEqualToValue,
  getOptionValue,
  isSearchedOption,
  onBlur,
  isMenuOpen = undefined,
}: Props) => {
  const animatedComponents = makeAnimated();

  const customStyles: StylesConfig<Option, boolean, GroupBase<Option>> = {
    option: (styles: any, { data, isDisabled, isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? "var(--surface-dark)"
        : isFocused
        ? "var(--surface)"
        : undefined,
      color: isDisabled
        ? undefined
        : isSelected
        ? "black"
        : isFocused
        ? undefined
        : undefined,
    }),

    menu: (styles: any) => ({
      ...styles,
      minWidth: 250,
      zIndex: 10000,
    }),

    container: (styles: any, { isFocused }) => ({
      ...styles,
      minWidth: 250,
      width: "100%",
      height: getInputHeight(size, true),
    }),

    control: (styles: any, { isFocused }) => ({
      ...styles,
      height: "100%",
      border: isFocused ? "2px solid var(--primary-dark)" : undefined,
      boxShadow: "none",
    }),

    valueContainer: (styles: any) => ({
      ...styles,
      border: "none",
      flexWrap: "nowrap",
      overflow: "scroll",
    }),

    multiValue: (styles: any) => ({
      ...styles,
      display: "flex",
    }),

    multiValueLabel: (styles: any) => ({
      ...styles,
      flex: 1,
    }),
  };

  const handleChange = (selectedOptions: any) => {
    if (selectedOptions.some((opt: any) => opt.value === "select-all")) {
      onChange(options); // Select all options
    } else {
      const allOptionsSelected = options.every((opt) =>
        selectedOptions.some((selected: any) => selected.value === opt.value)
      );
      if (allOptionsSelected) {
        onChange([
          ...selectedOptions,
          { label: "Select All", value: "select-all" },
        ]);
      } else {
        onChange(selectedOptions);
      }
    }
  };

  const selectAllOption = {
    label: "Select All Options",
    value: "select-all",
  };

  const modifiedOptions = [selectAllOption, ...options];

  return (
    <div className="relative bg-inherit">
      {label && (
        <label
          className={`text-slate-700 font-medium text-sm ${
            disabled && "opacity-40"
          } `}
        >
          {label} {required && <span className="text-red-500"> * </span>}
        </label>
      )}
      <Select
        menuIsOpen={isMenuOpen}
        name={name}
        value={value}
        onChange={handleChange}
        options={modifiedOptions}
        isOptionSelected={isOptionEqualToValue}
        styles={customStyles}
        isMulti
        isDisabled={disabled}
        isClearable
        isLoading={isLoading}
        placeholder={placeholder}
        className="mt-1 rounded border-1 border-divider"
        menuPosition="fixed"
        formatOptionLabel={(option: any, meta: FormatOptionLabelMeta<Option>) =>
          renderOption ? (
            renderOption(option, meta)
          ) : (
            <CustomOption data={option} />
          )
        }
        components={animatedComponents}
        classNamePrefix="custom-multi-select"
        getOptionValue={getOptionValue}
        filterOption={
          isSearchedOption
            ? (option, inputValue) =>
                isSearchedOption?.(option, inputValue?.toLowerCase())
            : undefined
        }
        onBlur={onBlur}
      />
      {name && (
        <ErrorMessage name={name}>
          {(errMsg) => (
            <p className="font-poppins absolute text-[14px] text-start mt-0 text-red-500">
              {errMsg}
            </p>
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default ATMMultiSelect;
